// NOVAPAY-116-RC3-R2 — session-only payment resume (no PII / no initData).
import type { PaymentMethodChoice } from "./checkout-validation-v2.js";

export const PAYMENT_RESUME_STORAGE_KEY = "store-v2-payment-resume-v1";

export type PaymentFlowState =
  | "order_created_unpaid"
  | "payment_session_creating"
  | "provider_open"
  | "payment_checking"
  | "payment_failed_or_cancelled"
  | "payment_paid_confirmed";

export type PaymentResumeV1 = {
  order_id: string;
  display_code: string | null;
  provider: PaymentMethodChoice;
  origin_route: "checkout" | "success";
  flow_state: PaymentFlowState;
  created_at: string;
  expires_at: string;
};

const TTL_MS = 2 * 60 * 60 * 1000;

function readRaw(): PaymentResumeV1 | null {
  try {
    const raw = window.sessionStorage.getItem(PAYMENT_RESUME_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PaymentResumeV1;
    if (!parsed?.order_id || !parsed.expires_at) return null;
    if (Date.now() > Date.parse(parsed.expires_at)) {
      window.sessionStorage.removeItem(PAYMENT_RESUME_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function hasActivePaymentResume(): boolean {
  return readRaw() != null;
}

export function readPaymentResume(): PaymentResumeV1 | null {
  return readRaw();
}

export function persistPaymentResume(input: {
  orderId: string;
  displayCode?: string | null;
  provider: PaymentMethodChoice;
  originRoute?: "checkout" | "success";
  flowState?: PaymentFlowState;
}): void {
  const now = Date.now();
  const payload: PaymentResumeV1 = {
    order_id: input.orderId,
    display_code: input.displayCode?.trim() || null,
    provider: input.provider,
    origin_route: input.originRoute ?? "success",
    flow_state: input.flowState ?? "order_created_unpaid",
    created_at: new Date(now).toISOString(),
    expires_at: new Date(now + TTL_MS).toISOString(),
  };
  try {
    window.sessionStorage.setItem(PAYMENT_RESUME_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function patchPaymentResumeFlowState(flowState: PaymentFlowState): void {
  const cur = readRaw();
  if (!cur) return;
  try {
    window.sessionStorage.setItem(
      PAYMENT_RESUME_STORAGE_KEY,
      JSON.stringify({ ...cur, flow_state: flowState }),
    );
  } catch {
    /* ignore */
  }
}

export function clearPaymentResume(): void {
  try {
    window.sessionStorage.removeItem(PAYMENT_RESUME_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
